// apps/share-web/src/player-view/setlist-player-wiring.ts
//
// Единый сетлист, фаза 3, задача D3 — проводка плеера: боковой список из
// `songs[]`, «Version ▾» в шапке, песня без записи. Вынесено в отдельный
// модуль по прямому требованию плана (`docs/superpowers/plans/
// 2026-09-24-unified-setlist-phases-3-4.md`, заметка «Дисциплина роста» —
// main.ts уже вырос на +78 строк за D1+D2, бюджет плана — 60 на всю
// веб-часть): вся логика — здесь, main.ts получает только вызов.
//
// Легаси-путь (сетлист БЕЗ `songs[]` — старый воркер, или сетлист,
// опубликованный до фазы 2) не тронут: `kind: 'legacy'` возвращает ровно то,
// что main.ts и так делает сегодня (`items`/`name`/`createdAt` в state,
// старая рельса `setlist-rail.ts`), и main.ts продолжает вести этот путь сам.
//
// Новый путь (`kind: 'songs'`) владеет:
//   - рельсой (`setlist-songs-rail.ts`) — ВСЕ песни, включая без записи;
//   - выбором версии на устройстве (`versions/version-choice.ts`) — какая
//     версия каждой песни СЕЙЧАС открыта (личный выбор или новейшая);
//   - «Version ▾» в шапке текущей песни (`version-picker.ts`, монтируется
//     через новый шов `mount.ts` `PlayerViewDeps.mountVersionActions`);
//   - переключением на песню без записи (`words-only-mount.ts`) — тот же
//     `#share-player-panel`, что несёт аудио-плеер, рельса остаётся.
//
// Личный выбор версии читается ЗАНОВО при каждом построении рельсы/пикера
// (`computeResolved`), а не один раз при вызове `wireSetlistPlayer` — иначе
// выбор внутри ТЕКУЩЕЙ сессии (пикер переключил версию) не отразился бы в
// рельсе без перезагрузки страницы: `resolveVersion` каждый раз видит
// актуальный `localStorage`.
import type { SetlistResult, SetlistItem, SetlistSong } from '../setlist.js';
import type { SongWordsResult } from '../song-text-api.js';
import { choiceKey, readChoice, writeChoice, resolveVersion } from '../versions/version-choice.js';
import {
  mountSetlistSongsRail,
  type SetlistSongsRailRow,
  type SetlistSongsRailHandle,
} from './setlist-songs-rail.js';
import {
  mountVersionPicker as mountVersionPickerReal,
  type VersionPickerItem,
  type VersionPickerVm,
  type VersionPickerHandle,
} from './version-picker.js';
import { mountWordsOnly as mountWordsOnlyReal, type WordsOnlySong } from './words-only-mount.js';
import { getState, setState, subscribe } from '../state.js';

export interface SetlistPlayerWiringDeps {
  fetchSetlist: (
    setlistId: string,
    apiBase: string,
    fetchImpl: typeof fetch,
    opts?: { guestKey?: string | null },
  ) => Promise<SetlistResult>;
  fetchSongText: (
    id: string,
    apiBase: string,
    fetchImpl: typeof fetch,
    guest?: { setlistId: string; guestKey: string },
  ) => Promise<SongWordsResult>;
  /** Личный выбор версии — `localStorage` в проде, замена в тестах. */
  storage: Pick<Storage, 'getItem' | 'setItem'>;
  /** Переключает плеер на РЕАЛЬНУЮ записанную версию — main.ts `loadSong`
   *  (запрос манифеста + `pushState`, `?setlist&g` переживают переход,
   *  поскольку `loadSong` берёт их из ТЕКУЩЕГО `location.search`). */
  navigate: (shareId: string) => void;
  // Три примитива ниже — из них ЭТОТ модуль (не main.ts) складывает переход
  // на песню без записи: монтаж words-only и URL-пометка (`&song=<position>`)
  // живут здесь (`selectWordsOnly`), main.ts даёт только доступ к своим
  // `disposeSong`/`currentAudioContext`/DOM-хосту, которые наружу целиком не
  // отдаёт (иначе main.ts получил бы обратно ту же функцию, что план требует
  // отсюда убрать — заметка о бюджете main.ts, план фаз 3-4).
  /** Снимает текущий регион (аудио-плеер ИЛИ прежний words-only) — main.ts'ов
   *  `disposeSong?.(); disposeSong=null` + закрытие AudioContext. */
  teardownSong: () => void | Promise<void>;
  /** Назначает, чем снимать СЛЕДУЮЩИЙ регион (`disposeSong = fn`). */
  setSongTeardown: (fn: () => void) => void;
  /** `#share-player-panel` — тот же хост, что несёт `mountPlayerView`.
   *  Функция, не значение: DOM ещё не смонтирован в момент вызова
   *  `wireSetlistPlayer` (шелл строится позже, `mountShell`). */
  getPlayerPanelHost: () => Element;
  /** Швы для тестов — в проде берутся реальные модули. */
  mountSongsRail?: typeof mountSetlistSongsRail;
  mountVersionPicker?: typeof mountVersionPickerReal;
  mountWordsOnly?: typeof mountWordsOnlyReal;
}

export interface WireSetlistPlayerOpts {
  setlistId: string;
  /** shareId из URL на момент вызова (main.ts уже валидировал его роутером). */
  routeShareId: string;
  guestKey: string | null;
  /** `null` — анонимный гость (тот же ключ выбора версии, что в `version-choice.ts`). */
  userId: string | null;
  apiBase: string;
  fetchImpl: typeof fetch;
  deps: SetlistPlayerWiringDeps;
}

export interface SongsRailMountHandle {
  unmount(): void;
}

export type SetlistPlayerWiring =
  | { kind: 'none' }
  | { kind: 'legacy'; items: SetlistItem[]; name: string | null; createdAt: string | null }
  | {
      kind: 'songs';
      /** Только песни С версией — форма `setlistItems` (state.ts), которую уже
       *  читает mount.ts для счётчика «N / M» и prev/next: без записи
       *  переход всё равно не в кого наводить. */
      items: SetlistItem[];
      name: string | null;
      createdAt: string | null;
      /** Монтирует рельсу (реактивна к сменам песни в этой же сессии). */
      mountRail(host: Element): SongsRailMountHandle;
      /** Строит `PlayerViewDeps.mountVersionActions` для ТЕКУЩЕЙ песни
       *  (найденной по её открытому shareId) — main.ts передаёт результат
       *  прямо в `mountPlayerView`. */
      mountVersionActionsFor(shareId: string): (host: HTMLElement) => { unmount(): void };
    };

interface ResolvedSong {
  song: SetlistSong;
  version: SetlistSong['versions'][number] | null;
}

/** Читает ЛИЧНЫЙ выбор версии заново для каждой песни — свежий `localStorage`,
 *  не снимок на момент вызова `wireSetlistPlayer` (см. шапку модуля). */
function computeResolved(
  songs: SetlistSong[],
  setlistId: string,
  userId: string | null,
  storage: Pick<Storage, 'getItem' | 'setItem'>,
): ResolvedSong[] {
  return songs.map((song) => {
    const key = song.songId ? choiceKey(userId, setlistId, song.songId) : null;
    const stored = key ? readChoice(storage, key) : null;
    const { version } = resolveVersion(song.versions, stored);
    return { song, version };
  });
}

/** `song=<position>` из текущего URL — какую строку без записи открыть, если
 *  ни одна версия не совпала с `activeShareId` (см. `setlist-songs-rail.ts`
 *  `activeRowIndex`). Читается напрямую из `location`, не через параметр
 *  конструктора: значение меняется В ТЕЧЕНИЕ сессии (открыл слова → рельса
 *  обязана подсветить их и без перезагрузки страницы). */
function wordsOnlyPositionFromLocation(): number | null {
  if (typeof location === 'undefined') return null;
  const raw = new URLSearchParams(location.search).get('song');
  if (!raw) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

export async function wireSetlistPlayer(opts: WireSetlistPlayerOpts): Promise<SetlistPlayerWiring> {
  const res = await opts.deps.fetchSetlist(opts.setlistId, opts.apiBase, opts.fetchImpl, {
    guestKey: opts.guestKey,
  });
  if (!res.ok) return { kind: 'none' };

  const name = res.setlist?.name ?? null;
  const createdAt = res.setlist?.createdAt ?? null;
  const songs = res.songs;

  if (!songs || songs.length === 0) {
    // Легаси: сетлист без `songs[]` (старый воркер) — main.ts ведёт его сам,
    // старой рельсой, байт-в-байт как сегодня.
    return { kind: 'legacy', items: res.items, name, createdAt };
  }

  const mountSongsRailFn = opts.deps.mountSongsRail ?? mountSetlistSongsRail;
  const mountVersionPickerFn = opts.deps.mountVersionPicker ?? mountVersionPickerReal;
  const mountWordsOnlyFn = opts.deps.mountWordsOnly ?? mountWordsOnlyReal;
  const { setlistId, userId, guestKey, apiBase, fetchImpl, deps } = opts;

  // `items` строится ОДИН раз здесь (а не на каждый рендер рельсы): счётчик
  // «N / M» и prev/next в mount.ts читают `state.setlistItems` в момент
  // монтажа КОНКРЕТНОЙ песни, а не реактивно — тот же контракт, что у items-
  // пути сегодня. Личный выбор к этому моменту уже применён.
  const initialResolved = computeResolved(songs, setlistId, userId, deps.storage);
  const items: SetlistItem[] = initialResolved
    .filter((r): r is ResolvedSong & { version: NonNullable<ResolvedSong['version']> } => r.version !== null)
    .map((r) => ({
      position: r.song.position,
      shareId: r.version.shareId,
      songId: r.song.songId,
      songName: r.song.songName,
    }));

  function buildRows(resolved: ResolvedSong[]): SetlistSongsRailRow[] {
    return resolved.map((r) => ({
      shareId: r.version?.shareId ?? null,
      songName: r.song.songName ?? '',
      key: r.song.words?.key ?? undefined,
    }));
  }

  /** Индекс строки БЕЗ записи, которую надо подсветить сейчас — см. шапку
   *  `wordsOnlyPositionFromLocation`. `null`, если такой строки нет (обычный
   *  аудио-путь: подсветка идёт по `activeShareId`, который несёт сама рельса). */
  function activeRowIndexOf(resolved: ResolvedSong[]): number | null {
    const pos = wordsOnlyPositionFromLocation();
    if (pos == null) return null;
    const idx = resolved.findIndex((r) => r.song.position === pos);
    return idx >= 0 && resolved[idx]!.version === null ? idx : null;
  }

  async function selectWordsOnly(rowIndex: number, resolved: ResolvedSong[]): Promise<void> {
    const entry = resolved[rowIndex];
    if (!entry) return;
    // Ни слов, ни записи (`song.words === null`) — минимальное состояние:
    // words-only-mount.ts само рисует «No recording — words only» + пунктир
    // «слов пока нет» на пустом `text`, ничего доп. не требуется (бриф D3,
    // пункт 4). Слова есть — фетчим текст чарта (guest-пропуск форвардится).
    const words = entry.song.words;
    let wordsSong: WordsOnlySong;
    if (!words) {
      wordsSong = { name: entry.song.songName ?? '', text: '' };
    } else {
      const guest = guestKey ? { setlistId, guestKey } : undefined;
      const textRes = await deps.fetchSongText(words.id, apiBase, fetchImpl, guest);
      wordsSong = {
        name: words.name || entry.song.songName || '',
        key: words.key ?? undefined,
        bpm: textRes.ok ? (textRes.payload.bpm || undefined) : undefined,
        text: textRes.ok ? (textRes.payload.chart?.text ?? '') : '',
      };
    }
    // Снять текущий регион (аудио ИЛИ прежний words-only), смонтировать
    // читалку в тот же хост, назначить её снятие следующим teardown'ом, и
    // пометить строку в URL (`&song=<position>`) — переживает reload/rail
    // resubscribe этой же сессии (`activeRowIndexOf` выше читает его же).
    // `state.activeShareId` идёт в null — иначе прежняя аудио-строка осталась
    // бы «NOW» рядом с новой words-only строкой (обе подсветки читаются
    // независимо в setlist-songs-rail.ts, activeShareId и activeRowIndex).
    setState({ activeShareId: null });
    await deps.teardownSong();
    if (typeof history !== 'undefined' && typeof location !== 'undefined') {
      const url = new URL(location.href);
      url.searchParams.set('song', String(entry.song.position));
      history.pushState({ wordsOnly: entry.song.position }, '', `${url.pathname}${url.search}`);
    }
    const handle = mountWordsOnlyFn(deps.getPlayerPanelHost(), wordsSong);
    deps.setSongTeardown(() => handle.unmount());
  }

  function selectVersion(song: SetlistSong, item: VersionPickerItem): void {
    if (song.songId) {
      writeChoice(deps.storage, choiceKey(userId, setlistId, song.songId), {
        shareId: item.shareId,
        sessionId: item.sessionId,
      });
    }
    deps.navigate(item.shareId);
  }

  return {
    kind: 'songs',
    items,
    name,
    createdAt,

    mountRail(host: Element): SongsRailMountHandle {
      let handle: SetlistSongsRailHandle | null = null;
      let latestResolved: ResolvedSong[] = initialResolved;

      // Какая песня была текущей на последней сборке — см. subscribe ниже.
      let renderedActive: string | null | undefined;

      const render = (): void => {
        handle?.unmount();
        renderedActive = getState().activeShareId;
        latestResolved = computeResolved(songs, setlistId, userId, deps.storage);
        handle = mountSongsRailFn(host, {
          rows: buildRows(latestResolved),
          // NOT `?? opts.routeShareId`: that fallback would re-pin the very
          // first route's share as permanently "now" once state.activeShareId
          // goes to null for words-only (below) — main.ts always sets
          // state.activeShareId before the very first mount, so the fallback
          // buys nothing and only causes two rows to show "now" at once.
          activeShareId: getState().activeShareId,
          activeRowIndex: activeRowIndexOf(latestResolved),
          name,
          createdAt,
          onSelect: (shareId) => deps.navigate(shareId),
          // `selectWordsOnly` не трогает `state` (main.ts не переиздаёт его
          // ради одной этой рельсы) — рендер здесь, СРАЗУ после монтажа
          // читалки, а не через subscribe: подписка ниже ловит только смены
          // РЕАЛЬНОЙ песни (`navigate`/`loadSong` пишет `state.activeShareId`).
          onSelectWordsOnly: (rowIndex) => { void selectWordsOnly(rowIndex, latestResolved).then(render); },
        });
      };
      render();

      // Реактивна к сменам РЕАЛЬНОЙ песни в этой же сессии: `navigate`
      // (main.ts `loadSong`) пишет `state.activeShareId`. Полная пересборка,
      // не точечная подсветка —
      // `setlist-songs-rail.ts` не несёт метода обновления одной карточки, и
      // ряды всё равно должны прочитать свежий личный выбор (смена версии).
      //
      // Только на смену ТЕКУЩЕЙ песни, не на каждый setState (Сергей 25.09:
      // «не переключаются мышкой песни»): playhead пишется в стор каждый кадр,
      // и пересборка на любое событие меняла карточку под курсором между
      // mousedown и mouseup — клик человека не доходил. Смена версии тоже
      // приходит сюда сменой activeShareId (navigate на shareId версии).
      const unsub = subscribe((s) => { if (s.activeShareId !== renderedActive) render(); });

      return {
        unmount(): void {
          unsub();
          handle?.unmount();
        },
      };
    },

    mountVersionActionsFor(shareId: string) {
      return (host: HTMLElement): { unmount(): void } => {
        // Свежий выбор на каждый монтаж песни — main.ts зовёт это при КАЖДОМ
        // swapSong, так что «сейчас открыто» всегда совпадает с song.version.
        const resolved = computeResolved(songs, setlistId, userId, deps.storage);
        const entry = resolved.find((r) => r.version?.shareId === shareId);
        if (!entry || entry.song.versions.length === 0) {
          return { unmount(): void {} };
        }
        const versions = entry.song.versions;
        const vm: VersionPickerVm = {
          items: versions.map((v) => ({
            shareId: v.shareId,
            sessionId: v.sessionId,
            label: v.label,
            recordedAt: v.recordedAt,
            isNewest: v === versions[0],
            isCurrent: v.shareId === entry.version?.shareId,
          })),
        };
        let picker: VersionPickerHandle | null = mountVersionPickerFn(host, vm, {
          onSelect: (item) => selectVersion(entry.song, item),
        });
        return {
          unmount(): void {
            picker?.unmount();
            picker = null;
          },
        };
      };
    },
  };
}
